import { useMemo } from 'react';
import { FiDollarSign, FiCheckCircle, FiAlertCircle, FiArchive } from 'react-icons/fi';
import { StatCard, StatCardGrid } from '../../../components/common';
import { fmtCurrency } from '../../../utils/formatNumber';
import { formatDate } from '../../../utils/dateUtils';
import styles from '../MemberReports.module.css';

const categoryName = (o) => o.DonationCategory?.name || o.donationCategoryId?.name || '—';
const personName   = (o) => o.member?.name || o.userId?.name || '—';

const lastPaidDate = (o) => {
  const dates = (o.settlements || []).map((s) => s.Receipt?.receiptDate).filter(Boolean).sort();
  return dates.length > 0 ? dates[dates.length - 1] : null;
};

const groupByPerson = (list) => {
  const map = new Map();
  list.forEach((o) => {
    const pid  = String(o.userId?.id ?? o.userId);
    const name = personName(o);
    if (!map.has(pid)) map.set(pid, { name, count: 0, offered: 0, paid: 0, pending: 0 });
    const e = map.get(pid);
    e.count++;
    e.offered  += Number(o.offeredAmount) || 0;
    e.paid     += Number(o.paidAmount)    || 0;
    e.pending  += Number(o.pendingAmount) || 0;
  });
  return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
};

const catSection = {
  background: 'var(--color-bg)',
  border: '1px solid var(--color-border)',
  borderRadius: 6,
  overflowX: 'auto',
  marginBottom: 16,
};

export default function OwnerLedgerReport({
  offerings,
  allDeps,
  selectedMemberId,
  currentFyId,
}) {
  const personIds = useMemo(() => {
    if (!selectedMemberId) return new Set();
    return new Set(
      allDeps
        .filter((p) => String(p.createdByMemberId) === String(selectedMemberId))
        .map((p) => String(p.id))
    );
  }, [allDeps, selectedMemberId]);

  const ownerOfferings = useMemo(
    () => offerings.filter((o) => personIds.has(String(o.userId?.id ?? o.userId))),
    [offerings, personIds]
  );

  const pendingOfferings = useMemo(
    () => ownerOfferings
      .filter((o) => o.status === 'pending' || o.status === 'partial')
      .sort((a, b) => (a.offeringDate || '').localeCompare(b.offeringDate || '')),
    [ownerOfferings]
  );

  const paidOfferings = useMemo(
    () => ownerOfferings
      .filter((o) => {
        const fyId = String(o.financialYearId?.id ?? o.financialYearId ?? '');
        return String(currentFyId) === fyId && Number(o.paidAmount) > 0;
      })
      .sort((a, b) => {
        const da = lastPaidDate(a) || '';
        const db = lastPaidDate(b) || '';
        return db.localeCompare(da);
      }),
    [ownerOfferings, currentFyId]
  );

  const pendingByPerson = useMemo(() => groupByPerson(pendingOfferings), [pendingOfferings]);
  const paidByPerson    = useMemo(() => groupByPerson(paidOfferings),    [paidOfferings]);

  const totPending = pendingOfferings.reduce((s, o) => s + (Number(o.pendingAmount) || 0), 0);
  const totPaid    = paidOfferings.reduce((s, o)    => s + (Number(o.paidAmount)    || 0), 0);

  const SectionHead = ({ label }) => (
    <div style={{
      background: 'var(--color-primary, #1565C0)',
      borderRadius: 4,
      padding: '6px 10px',
      marginBottom: 10,
    }}>
      <strong style={{ fontSize: '0.82rem', color: '#fff' }}>{label}</strong>
    </div>
  );

  const SubLabel = ({ label }) => (
    <div style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--color-text-muted)', marginBottom: 4, paddingLeft: 2 }}>
      {label}
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {!selectedMemberId && (
        <div className="card">
          <div className="empty-state">Select a member from the filter bar above to view all registered persons' offerings.</div>
        </div>
      )}

      {selectedMemberId && ownerOfferings.length === 0 && (
        <div className="card">
          <div className="empty-state">No offering records found for persons under this member.</div>
        </div>
      )}

      {selectedMemberId && ownerOfferings.length > 0 && (
        <>
          <StatCardGrid cols={4}>
            <StatCard
              icon={<FiArchive size={18} />}
              iconBg="rgba(239,68,68,0.12)"
              iconColor="#ef4444"
              label="Pending Entries"
              value={String(pendingOfferings.length)}
            />
            <StatCard
              icon={<FiAlertCircle size={18} />}
              iconBg="rgba(239,68,68,0.12)"
              iconColor="#ef4444"
              label="Total Pending"
              value={fmtCurrency(totPending)}
              valueColor="red"
            />
            <StatCard
              icon={<FiDollarSign size={18} />}
              iconBg="rgba(16,185,129,0.12)"
              iconColor="#10b981"
              label="Paid Entries (This Year)"
              value={String(paidOfferings.length)}
            />
            <StatCard
              icon={<FiCheckCircle size={18} />}
              iconBg="rgba(16,185,129,0.12)"
              iconColor="#10b981"
              label="Total Paid (This Year)"
              value={fmtCurrency(totPaid)}
              valueColor="green"
            />
          </StatCardGrid>

          {/* Pending & Partial section */}
          <div className="card">
            <SectionHead label="Pending & Partial — All Financial Years" />

            <SubLabel label="By Person" />
            <div style={catSection}>
              <table>
                <thead>
                  <tr>
                    <th style={{ width: 36 }}>#</th>
                    <th>Person</th>
                    <th style={{ width: 70, textAlign: 'center' }}>Entries</th>
                    <th className={styles.amtRight}>Offered (₹)</th>
                    <th className={styles.amtRight}>Paid (₹)</th>
                    <th className={styles.amtRight}>Pending (₹)</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingByPerson.length === 0
                    ? <tr><td colSpan={6} className="empty-state">No pending entries</td></tr>
                    : pendingByPerson.map((p, i) => (
                        <tr key={p.name}>
                          <td>{i + 1}</td>
                          <td><strong>{p.name}</strong></td>
                          <td style={{ textAlign: 'center' }}>{p.count}</td>
                          <td className={styles.amtRight}>{fmtCurrency(p.offered)}</td>
                          <td className={styles.amtRight}>{fmtCurrency(p.paid)}</td>
                          <td className={`${styles.amtRight} ${p.pending > 0 ? styles.pending : ''}`}>
                            <strong>{fmtCurrency(p.pending)}</strong>
                          </td>
                        </tr>
                      ))
                  }
                </tbody>
                {pendingByPerson.length > 0 && (
                  <tfoot>
                    <tr>
                      <td colSpan={2}><strong>Total ({pendingByPerson.length} persons)</strong></td>
                      <td style={{ textAlign: 'center' }}><strong>{pendingOfferings.length}</strong></td>
                      <td className={styles.amtRight}><strong>{fmtCurrency(pendingByPerson.reduce((s, p) => s + p.offered, 0))}</strong></td>
                      <td className={styles.amtRight}><strong>{fmtCurrency(pendingByPerson.reduce((s, p) => s + p.paid, 0))}</strong></td>
                      <td className={`${styles.amtRight} ${styles.pending}`}><strong>{fmtCurrency(totPending)}</strong></td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>

            <SubLabel label="All Entries" />
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th style={{ width: 36 }}>#</th>
                    <th>Person</th>
                    <th>Category</th>
                    <th>Offering Date</th>
                    <th className={styles.amtRight}>Offered (₹)</th>
                    <th className={styles.amtRight}>Paid (₹)</th>
                    <th className={styles.amtRight}>Pending (₹)</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingOfferings.length === 0
                    ? <tr><td colSpan={8} className="empty-state">No pending or partial entries found.</td></tr>
                    : pendingOfferings.map((o, i) => (
                        <tr key={o.id ?? i}>
                          <td>{i + 1}</td>
                          <td>{personName(o)}</td>
                          <td>{categoryName(o)}</td>
                          <td>{formatDate(o.offeringDate)}</td>
                          <td className={styles.amtRight}>{fmtCurrency(o.offeredAmount)}</td>
                          <td className={styles.amtRight}>{fmtCurrency(o.paidAmount)}</td>
                          <td className={`${styles.amtRight} ${Number(o.pendingAmount) > 0 ? styles.pending : ''}`}>
                            <strong>{fmtCurrency(o.pendingAmount)}</strong>
                          </td>
                          <td>
                            <span className={`badge badge-${o.status === 'partial' ? 'submitted' : 'pending'}`}>
                              {o.status}
                            </span>
                          </td>
                        </tr>
                      ))
                  }
                </tbody>
                {pendingOfferings.length > 0 && (
                  <tfoot>
                    <tr>
                      <td colSpan={4}><strong>Total ({pendingOfferings.length} entries)</strong></td>
                      <td className={styles.amtRight}><strong>{fmtCurrency(pendingOfferings.reduce((s, o) => s + (Number(o.offeredAmount) || 0), 0))}</strong></td>
                      <td className={styles.amtRight}><strong>{fmtCurrency(pendingOfferings.reduce((s, o) => s + (Number(o.paidAmount) || 0), 0))}</strong></td>
                      <td className={`${styles.amtRight} ${styles.pending}`}><strong>{fmtCurrency(totPending)}</strong></td>
                      <td />
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>

          {/* Paid section */}
          <div className="card">
            <SectionHead label="Paid — Current Financial Year" />

            <SubLabel label="By Person" />
            <div style={catSection}>
              <table>
                <thead>
                  <tr>
                    <th style={{ width: 36 }}>#</th>
                    <th>Person</th>
                    <th style={{ width: 70, textAlign: 'center' }}>Entries</th>
                    <th className={styles.amtRight}>Offered (₹)</th>
                    <th className={styles.amtRight}>Paid (₹)</th>
                  </tr>
                </thead>
                <tbody>
                  {paidByPerson.length === 0
                    ? <tr><td colSpan={5} className="empty-state">No paid entries this year</td></tr>
                    : paidByPerson.map((p, i) => (
                        <tr key={p.name}>
                          <td>{i + 1}</td>
                          <td><strong>{p.name}</strong></td>
                          <td style={{ textAlign: 'center' }}>{p.count}</td>
                          <td className={styles.amtRight}>{fmtCurrency(p.offered)}</td>
                          <td className={styles.amtRight}>{fmtCurrency(p.paid)}</td>
                        </tr>
                      ))
                  }
                </tbody>
                {paidByPerson.length > 0 && (
                  <tfoot>
                    <tr>
                      <td colSpan={2}><strong>Total ({paidByPerson.length} persons)</strong></td>
                      <td style={{ textAlign: 'center' }}><strong>{paidOfferings.length}</strong></td>
                      <td className={styles.amtRight}><strong>{fmtCurrency(paidByPerson.reduce((s, p) => s + p.offered, 0))}</strong></td>
                      <td className={styles.amtRight}><strong>{fmtCurrency(totPaid)}</strong></td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>

            <SubLabel label="All Entries" />
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th style={{ width: 36 }}>#</th>
                    <th>Person</th>
                    <th>Category</th>
                    <th>Offering Date</th>
                    <th>Paid Date</th>
                    <th className={styles.amtRight}>Offered (₹)</th>
                    <th className={styles.amtRight}>Paid (₹)</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {paidOfferings.length === 0
                    ? <tr><td colSpan={8} className="empty-state">No payments recorded this year.</td></tr>
                    : paidOfferings.map((o, i) => (
                        <tr key={o.id ?? i}>
                          <td>{i + 1}</td>
                          <td>{personName(o)}</td>
                          <td>{categoryName(o)}</td>
                          <td>{formatDate(o.offeringDate)}</td>
                          <td>{formatDate(lastPaidDate(o)) || '—'}</td>
                          <td className={styles.amtRight}>{fmtCurrency(o.offeredAmount)}</td>
                          <td className={styles.amtRight}>{fmtCurrency(o.paidAmount)}</td>
                          <td>
                            <span className={`badge badge-${
                              o.status === 'completed' ? 'approved' :
                              o.status === 'partial'   ? 'submitted' : 'pending'
                            }`}>
                              {o.status}
                            </span>
                          </td>
                        </tr>
                      ))
                  }
                </tbody>
                {paidOfferings.length > 0 && (
                  <tfoot>
                    <tr>
                      <td colSpan={5}><strong>Total ({paidOfferings.length} entries)</strong></td>
                      <td className={styles.amtRight}><strong>{fmtCurrency(paidOfferings.reduce((s, o) => s + (Number(o.offeredAmount) || 0), 0))}</strong></td>
                      <td className={styles.amtRight}><strong>{fmtCurrency(totPaid)}</strong></td>
                      <td />
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
